import { useState } from "react";
import ActionSheet from "../ActionSheet";
import SearchInput from "@/component/SearchInput";
import Checkbox from "@/component/Checkbox";
import RightsSheet from "../RightsSheet";
import { getSpeakerAvatarInfo } from "@/utils/newAvatar";
import Avatar from "@/component/Avatar";
import { avatarBgs } from "@/constants/record";
import useGetStaffList from "@/sections/Share/useGetStaffList";
import { IStaffItem, MeetingSharesItem } from "@/types/share";
import { RoleType } from "@/constants/share";
import IC_NO_STAFF from "@/assets/common/ic_no_staff.png";
import IC_NODE from "@/assets/common/ic_none.png";
import "./index.css";

function AddressBook(props: {
  onChange: (value: { checked: boolean; value: MeetingSharesItem }) => void;
  selected?: MeetingSharesItem[];
  shareId?: string;
}) {
  const { onChange, shareId } = props;
  const { keyword, staffList, setKeyword } = useGetStaffList(shareId ?? "");

  const handleChange = (checked: boolean, staff: MeetingSharesItem) => {
    onChange?.({ checked, value: staff });
  };

  const handleChecked = (user: IStaffItem) => {
    return props.selected?.some((item) => item.identityId === user.identityId);
  };

  return (
    <div className="flex-1 flex flex-col justify-start items-center flex-shrink-0 overflow-hidden">
      <SearchInput placeholder="输名字搜索通讯录成员" inputClassName="placeholder:text-#A1A7B2 text-size-3.5" onChange={setKeyword} className="flex-shrink-0" />
      {staffList.length === 0 ?
        (<div className="w-full flex flex-col flex-justify-center flex-items-center mt-45">
          <img src={keyword ? IC_NO_STAFF : IC_NODE} className="w-20 h-20" />
          <span className="mt-4 h-5 lh-5 text-mx-quaternary text-3.5">
            {keyword ? "未能找到相关结果" : "暂无记录"}
          </span>
        </div>) :
        (<div className="w-full h-full m-t-3 overflow-auto">
          {staffList.map((staff, index) => {
            const staffAvatarInfo = getSpeakerAvatarInfo(staff.avatar);
            const shareInfo: MeetingSharesItem = {
              identityId: staff.identityId,
              name: staff.realname,
              avatar: staff.avatar,
              sharePermission: RoleType.Viewer
            };
            return (
              <Checkbox
                key={staff.identityId}
                disabled={staff.subscribed}
                wrapperClassName="flex items-center p-x-6 m-b-2 h-12.5 staff-checkbox"
                contentClassName="flex-1 flex items-center m-l-2"
                onChange={(e) => handleChange(e.target.checked, shareInfo)}
                checked={staff.subscribed || handleChecked(staff)}
              >
                <Avatar
                  className="w-8 h-8 text-3 font-600 ml--1"
                  key={staff.identityId}
                  userInfo={{
                    userId: staff.identityId,
                    displayName: staff.realname,
                    avatarUrl: staffAvatarInfo.avatarUrl,
                    avatarIndex: staffAvatarInfo.avatarIndex || index % avatarBgs.length,
                  }}
                />
                <div className="m-l-4 text-size-3.5 text-#30343C">{staff.realname}</div>
              </Checkbox>
            );
          })}
        </div>
        )}
    </div>
  );
}

function SelectedUsers(props: {
  mySharePermission: RoleType; // 自己的角色
  selectedUsers: MeetingSharesItem[];
  onChangeSelected: (value: MeetingSharesItem) => void;
}) {
  const { selectedUsers, onChangeSelected, mySharePermission } = props;

  const handleRightsChange = (value: MeetingSharesItem) => {
    onChangeSelected?.(value);
  };

  return (
    <>
      <div className="flex-1 flex flex-col justify-start items-center overflow-hidden">
        <div className="w-full m-t-3 overflow-auto">
          {selectedUsers.map((staff, index) => {
            const staffAvatarInfo = getSpeakerAvatarInfo(staff.avatar);
            const shareInfo: MeetingSharesItem = {
              identityId: staff.identityId,
              name: staff.name,
              avatar: staff.avatar,
              sharePermission: staff.sharePermission
            };
            return (
              <div
                key={staff.identityId}
                className="flex items-center justify-between p-x-6 m-b-2 h-12.5"
              >
                <div className="flex items-center">
                  <Avatar
                    className="w-8 h-8 text-3 font-600 ml--1"
                    key={staff.identityId}
                    userInfo={{
                      userId: staff.identityId,
                      displayName: staff.name,
                      avatarUrl: staffAvatarInfo.avatarUrl,
                      avatarIndex: staffAvatarInfo.avatarIndex || index % avatarBgs.length,
                    }}
                  />
                  <div className="m-l-4 text-size-3.5 text-#30343C">{staff.name}</div>
                </div>
                <RightsSheet user={shareInfo}
                  onChange={handleRightsChange}
                  editable={mySharePermission === RoleType.Creator || mySharePermission === RoleType.Manager}
                />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function AddressSheet(props: {
  visible: boolean;
  shareId?: string;
  mySharePermission: RoleType;
  onClose?: () => void;
  onAdded?: (users: MeetingSharesItem[]) => void;
}) {
  const { visible, onClose, onAdded, shareId, mySharePermission } = props;
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedUsers, setSelectedUsers] = useState<MeetingSharesItem[]>([]);

  const handleClose = () => {
    if (step === 2) {
      setStep(1);
    } else {
      setSelectedUsers([]);
      onClose?.();
    }
  };

  const handleSelectedChange = (value: { checked: boolean; value: MeetingSharesItem }) => {
    if (value.checked) {
      setSelectedUsers((prev) => [...prev, value.value]);
    } else {
      setSelectedUsers((prev) =>
        prev.filter((user) => user.identityId !== value.value.identityId)
      );
    }
  };

  const handleChangeSelected = (value: MeetingSharesItem) => {
    if (value.sharePermission === RoleType.Removed) {
      setSelectedUsers((prev) => prev.filter((user) => user.identityId !== value.identityId));
      if (selectedUsers.length === 1) setStep(1);
      return;
    } else {
      setSelectedUsers((prev) =>
        prev.map((user) => (user.identityId === value.identityId ? value : user))
      );
    }
  };

  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    } else {
      console.log("添加用户", selectedUsers);
      onAdded?.(selectedUsers);
      onClose?.();
      setSelectedUsers([]);
      setStep(1);
    }
  };

  return (
    <ActionSheet
      title="从通讯录中添加"
      showReturn={step === 2}
      className={`top-10`}
      onClose={handleClose}
      visible={visible}
      contentClassName="bg-mx-white flex flex-col overflow-hidden"
    >
      {step === 1 ? (
        <AddressBook
          onChange={handleSelectedChange}
          selected={selectedUsers}
          shareId={shareId} />
      ) : (
        <SelectedUsers
          selectedUsers={selectedUsers}
          mySharePermission={mySharePermission}
          onChangeSelected={handleChangeSelected}
        />
      )}
      <div className="h-22.5">
        <div className="h-14 flex items-center justify-between p-x-6 text-size-4 line-height-4 b-0 b-t-1 b-solid b-#ECEEF3">
          <div className="color-mx-primary">已选：{selectedUsers.length}个</div>
          <button
            className={`w-25 h-8 mx-btn-base bg-mx-primary ${selectedUsers.length === 0 ? "op-30" : ""} color-mx-white rd-2`}
            onClick={handleNext}
            disabled={selectedUsers.length === 0}
          >
            {step === 1 ? " 下一步" : "添加"}
          </button>
        </div>
      </div>
    </ActionSheet>
  );
}

export default AddressSheet;
